import React, { useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { Sun, Moon, ChevronDown, ArrowRight, ChevronRight } from 'lucide-react';
import solutionContent, { solutionCategories } from '../../data/solutionContent';

import BrandLogo from '../common/BrandLogo';
const PublicNavbar = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsHovered, setIsSolutionsHovered] = useState(false);

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  const megaMenuVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.2, ease: 'easeIn' } }
  };

  const mobileMenuVariants = {
    closed: { x: '100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const navLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Advisory', path: '/advisory' },
    { name: 'Insights', path: '/insights' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav 
      variants={navVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem 5%', position: 'fixed', top: 0, left: 0, right: 0,
        background: 'var(--bg-modal)', backdropFilter: 'blur(25px)', zIndex: 1000,
        borderBottom: '1px solid var(--border-color)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
        height: 'var(--topbar-height)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
        <BrandLogo />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
        {/* Desktop Links */}
        <div style={{ display: 'flex', gap: '1.5rem', fontWeight: '500', fontSize: '0.9rem' }} className="d-desktop-only">
          <Link to="/about" style={{ color: 'var(--text-primary)', textDecoration: 'none', transition: 'color 0.2s', padding: '0.5rem 0' }}>About Us</Link>
          
          <div 
            style={{ position: 'relative' }}
            onMouseEnter={() => setIsSolutionsHovered(true)}
            onMouseLeave={() => setIsSolutionsHovered(false)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--text-primary)', cursor: 'pointer', padding: '0.5rem 0' }} onClick={() => navigate('/solutions')}>
              Solutions <ChevronDown size={14} style={{ transform: isSolutionsHovered ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </div>

            <AnimatePresence>
              {isSolutionsHovered && (
                <motion.div
                  variants={megaMenuVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                    width: '850px', background: 'var(--bg-primary)', backdropFilter: 'blur(20px)',
                    border: '1px solid var(--border-color)', borderRadius: '16px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)', padding: '2rem',
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2rem',
                    marginTop: '1rem', paddingTop: '2rem'
                  }}
                >
                  <div style={{ position: 'absolute', top: '-1rem', left: 0, right: 0, height: '1rem' }} />
                  {solutionCategories.map((category) => (
                    <div key={category.name}>
                      <h4 style={{ color: 'var(--accent-primary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                        {category.name}
                      </h4>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                        {category.ids.map(id => {
                          const sol = solutionContent[id];
                          return (
                            <li key={id}>
                              <Link to={`/solutions/${id}`} onClick={() => setIsSolutionsHovered(false)} style={{ textDecoration: 'none', color: 'var(--text-primary)', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', transition: 'all 0.2s', opacity: 0.85 }}>
                                <span style={{ fontSize: '1rem' }}>{sol.emoji}</span>
                                <div style={{ fontWeight: '600', fontSize: '0.85rem' }}>{sol.title}</div>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                     <Link to="/solutions" onClick={() => setIsSolutionsHovered(false)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: '700', fontSize: '0.85rem' }}>
                        Explore All Solutions <ArrowRight size={14} />
                     </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/advisory" style={{ color: 'var(--text-primary)', textDecoration: 'none', transition: 'color 0.2s', padding: '0.5rem 0' }}>Advisory</Link>
          <Link to="/insights" style={{ color: 'var(--text-primary)', textDecoration: 'none', transition: 'color 0.2s', padding: '0.5rem 0' }}>Insights</Link>
          <Link to="/contact" style={{ color: 'var(--text-primary)', textDecoration: 'none', transition: 'color 0.2s', padding: '0.5rem 0' }}>Contact</Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <button onClick={toggleTheme} style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', 
            color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', 
            alignItems: 'center', justifyContent: 'center', padding: '0.5rem',
            borderRadius: '50%', transition: 'all 0.2s'
          }}>
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          
          <Link to="/login" className="d-desktop-only" style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>Sign In</Link>
          
          <button 
            className="d-desktop-only"
            onClick={() => navigate('/register')}
            style={{ 
              background: 'var(--text-primary)', color: 'var(--bg-primary)', padding: '0.5rem 1.2rem', 
              borderRadius: '8px', border: 'none', fontWeight: '700', fontSize: '0.9rem',
              cursor: 'pointer', transition: 'all 0.2s'
            }}
          >
            Get Started
          </button>

          {/* Hamburger Toggle */}
          <button 
            className="d-mobile-only"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              background: 'none', border: 'none', color: 'var(--text-primary)',
              cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center'
            }}
          >
            <div style={{ width: '24px', height: '18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <span style={{ width: '100%', height: '2px', background: 'currentColor', borderRadius: '2px', transform: isMobileMenuOpen ? 'translateY(8px) rotate(45deg)' : 'none', transition: '0.3s' }} />
              <span style={{ width: '100%', height: '2px', background: 'currentColor', borderRadius: '2px', opacity: isMobileMenuOpen ? 0 : 1, transition: '0.3s' }} />
              <span style={{ width: '100%', height: '2px', background: 'currentColor', borderRadius: '2px', transform: isMobileMenuOpen ? 'translateY(-8px) rotate(-45deg)' : 'none', transition: '0.3s' }} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 998 }}
            />
            <motion.div
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: '80%', maxWidth: '320px',
                background: 'var(--bg-modal)', backdropFilter: 'blur(20px)', zIndex: 999,
                padding: '5rem 2rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem',
                borderLeft: '1px solid var(--border-color)', boxShadow: '-10px 0 30px rgba(0,0,0,0.2)'
              }}
            >
              {navLinks.map((link, idx) => (
                <Link 
                  key={link.name} to={link.path} 
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ color: 'var(--text-primary)', textDecoration: 'none', fontSize: '1.2rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  {link.name} <ChevronRight size={18} />
                </Link>
              ))}
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Link 
                  to="/login" onClick={() => setIsMobileMenuOpen(false)}
                  style={{ textAlign: 'center', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border-color)', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: '700' }}
                >
                  Sign In
                </Link>
                <button 
                  onClick={() => { navigate('/register'); setIsMobileMenuOpen(false); }}
                  style={{ padding: '1rem', borderRadius: '12px', background: 'var(--accent-gradient)', color: '#000', border: 'none', fontWeight: '800', cursor: 'pointer' }}
                >
                  Get Started
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default PublicNavbar;
